import { type FormEvent, useState } from "react";
import { AuthError, signIn, signUp } from "../lib/api/auth";

/**
 * Sign in.
 *
 * One plate, no panes — the only screen in the product where a narrow centred plate is
 * right, because there is exactly one thing to do. The shell frame stays, so this does not
 * read as a different application from the one behind it.
 */
export function SignIn({ onDone }: { onDone: () => void }) {
  const [mode, setMode] = useState<"signin" | "signup">("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      if (mode === "signup") await signUp({ email, password, name });
      else await signIn({ email, password });
      onDone();
    } catch (err) {
      // Say what is wrong and what to do, never "authentication failed".
      const code = err instanceof AuthError ? err.code : null;
      setError(
        code === "INVALID_EMAIL_OR_PASSWORD"
          ? "That email and password do not match an account."
          : code === "USER_ALREADY_EXISTS_USE_ANOTHER_EMAIL"
            ? "There is already an account with that email. Sign in instead."
            : err instanceof Error
              ? err.message
              : "That did not work. Try again in a moment.",
      );
    } finally {
      setBusy(false);
    }
  }

  const isSignup = mode === "signup";

  return (
    <div className="auth-form">
      <h1 className="signin-title">{isSignup ? "Create your account" : "Sign in"}</h1>
      <p className="signin-sub">
        {isSignup
          ? "You will connect your own WhatsApp number in the next few minutes."
          : "Welcome back."}
      </p>

      <form className="fields" onSubmit={submit}>
        {isSignup && (
          <label className="field">
            <span>Your name</span>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoComplete="name"
              required
            />
          </label>
        )}

        <label className="field">
          <span>Email</span>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            required
          />
        </label>

        <label className="field">
          <span>Password</span>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete={isSignup ? "new-password" : "current-password"}
            minLength={8}
            required
          />
          {isSignup && <small>At least eight characters.</small>}
        </label>

        {error && (
          <p className="formerror" role="alert">
            {error}
          </p>
        )}

        <button type="submit" className="btn primary" disabled={busy}>
          {busy ? "One moment…" : isSignup ? "Create account" : "Sign in"}
        </button>
      </form>

      <button
        type="button"
        className="linkbtn"
        onClick={() => {
          setMode(isSignup ? "signin" : "signup");
          setError(null);
        }}
      >
        {isSignup ? "I already have an account" : "Create an account instead"}
      </button>
    </div>
  );
}
